import { Platform } from "react-native";

export type ColorType =
  | "primary"
  | "secondary"
  | "surface"
  | "surfaceBorder"
  | "background"
  | "negative"
  | "semiNegative"
  | "positive"
  | "neutral"
  | "washedText"
  | "lightText"
  | "darkText"
  | "darkerText"
  | "transparent"
  | "usersIcon"
  | "surface-backgroundControl"
  | "surface-dark"
  | "surface-background"
  | "surface-cta"
  | "surface-primary"
  | "text-inverted"
  | "text-washed"
  | "text-secondary"
  | "text-primary"
  | "text-cta"
  | "status-inactive"
  | "status-positive"
  | "status-active"
  | "status-semiNegative"
  | "status-negative"
  | "border-cta"
  | "border-primary"
  | "border-strong"
  | "track-background";

type Shadow =
  | {
      shadowColor: string;
      shadowOffset: { width: number; height: number };
      shadowOpacity: number;
      shadowRadius: number;
    }
  | { elevation: number };

export const colorScheme = [
  "#43DD87",
  "#F06072",
  "#6290EC",
  "#F5A623",
  "#A97AEC",
  "#57D9C4",
  "#FF92E8",
  "#CA1616",
  "#1115BA",
  "#B77878",
  "#6D826C",
  "#EC620A",
  "#80AEC1",
  "#764E2B",
  "#4F4F4F",
  "#F2A1A1",
];
const colorPrimitives = {
  "tag-black": "#4f4f4f",
  "tag-darkbrown": "#764e2b",
  "tag-grayblue": "#80aec1",
  "tag-bronze": "#ec620a",
  "tag-forrest": "#6d826c",
  "tag-darkred": "#ca1616",
  "tag-orange": "#f5a623",
  "tag-red": "#f06072",
  "tag-green": "#43dd87",
  "tag-pig": "#f2a1a1",
  "tag-blue": "#6290ec",
  "tag-earth": "#b77878",
  "tag-darkblue": "#1115ba",
  "tag-pink": "#ff92e8",
  "tag-turquiose": "#57d9c4",
  "tag-purple": "#a97aec",
  teamColors: {
    "team-pastelblue": "#acc6d2",
    "team-yellow": "#ffdb5a",
    "team-orange": "#ff9e58",
    "team-red": "#ff9999",
    "team-purple": "#cd7cff",
    "team-lightpurple": "#c0ceff",
    "team-greengrass": "#bcef88",
    "team-lightgreen": "#ade0c8",
    "team-forestgreen": "#77ca8a",
    "team-lightblue": "#93dfff",
    "team-turquoise": "#43ddc1",
    "team-dark": "#616161",
    "team-blue": "#2d8dff",
    "team-lightgray": "#d1d1d1",
    "team-pink": "#f89eef",
    "team-gold": "#e8c289",
  },
  color: {
    gray: {
      0: "#ffffff",
      25: "#f6f6f6",
      50: "#f1f1f1",
      200: "#d3d3d3",
      400: "#afafaf",
      600: "#7c7c7c",
      800: "#343434",
      900: "#202020",
      1000: "#000000",
    },
    brand: {
      "blue-light": "#b5d2ff",
      blue: "#0066ff",
    },
    utility: {
      green: "#51e066",
      yellow: "#ffdc69",
      red: "#ff435a",
    },
  },
};

export const themeColors = {
  "surface-backgroundControl": colorPrimitives["color"]["gray"]["50"],
  "surface-dark": colorPrimitives["color"]["gray"]["1000"],
  "surface-background": colorPrimitives["color"]["gray"]["25"],
  "surface-cta": colorPrimitives["color"]["brand"]["blue"],
  "surface-primary": colorPrimitives["color"]["gray"]["0"],
  "text-inverted": colorPrimitives["color"]["gray"]["0"],
  "text-washed": colorPrimitives["color"]["gray"]["400"],
  "text-secondary": colorPrimitives["color"]["gray"]["600"],
  "text-primary": colorPrimitives["color"]["gray"]["1000"],
  "text-cta": colorPrimitives["color"]["brand"]["blue"],
  "status-inactive": colorPrimitives["color"]["gray"]["400"],
  "status-positive": colorPrimitives["color"]["utility"]["green"],
  "status-active": colorPrimitives["color"]["brand"]["blue"],
  "status-semiNegative": colorPrimitives["color"]["utility"]["yellow"],
  "status-negative": colorPrimitives["color"]["utility"]["red"],
  "border-cta": colorPrimitives["color"]["brand"]["blue"],
  "border-primary": colorPrimitives["color"]["gray"]["50"],
  "border-primary-legacy": colorPrimitives["color"]["gray"]["200"],
  "border-strong": colorPrimitives["color"]["gray"]["200"],
  "track-background": colorPrimitives.color.brand["blue-light"],
};

export type AHThemeType = Record<ColorType, string> & {
  getColor: (number: number) => string;
  colorScheme: string[];
  shadow: {
    weak: Shadow;
    medium: Shadow;
    strong: Shadow;
  };
};

const radiusPrimitives = {
  "3xl": 360,
  "2xl": 128,
  xl: 32,
  lg: 16,
  md: 8,
  sm: 4,
};

const radius = {
  "radius-large": radiusPrimitives["lg"],
  "radius-full": radiusPrimitives["3xl"],
  "radius-medium": radiusPrimitives["md"],
  "radius-minimal": radiusPrimitives["sm"],
};

const spacingScale = [
  4, 8, 16, 24, 32, 40, 48, 56, 64, 72, 80, 88, 96, 104, 128, 198, 256, 320,
  384, 448, 512,
];

const spacing = {
  "spacing-2xl": spacingScale[13],
  "spacing-lg": spacingScale[4],
  "spacing-md": spacingScale[2],
  "spacing-sm": spacingScale[1],
  "spacing-xl": spacingScale[6],
  "spacing-xs": spacingScale[0],
  "spacing-none": 0,
};

const lightTheme: AHThemeType = {
  primary: themeColors["status-active"],
  secondary: "rgb(255, 220, 105)",
  negative: themeColors["status-negative"],
  semiNegative: "rgb(255, 220, 105)",
  positive: themeColors["status-positive"],
  neutral: "#8B8B8B",
  lightText: themeColors["text-inverted"],
  darkerText: themeColors["text-primary"],
  darkText: themeColors["text-secondary"],
  washedText: themeColors["text-washed"],
  surface: themeColors["surface-primary"],
  surfaceBorder: themeColors["border-primary-legacy"], // depricated use border-primary
  background: themeColors["surface-background"],
  transparent: "transparent",
  usersIcon: "#F5A623",
  ...themeColors,
  shadow: {
    weak:
      Platform.OS === "android"
        ? { elevation: 3 }
        : {
            shadowColor: "#000",
            shadowOffset: { width: 0, height: 0 },
            shadowOpacity: 0.1,
            shadowRadius: 3,
          },
    medium:
      Platform.OS === "android"
        ? { elevation: 5 }
        : {
            shadowColor: "#000",
            shadowOffset: { width: 0, height: 0 },
            shadowOpacity: 0.1,
            shadowRadius: 5,
          },
    strong:
      Platform.OS === "android"
        ? { elevation: 7 }
        : {
            shadowColor: "#000",
            shadowOffset: { width: 0, height: 0 },
            shadowOpacity: 0.1,
            shadowRadius: 15,
          },
  },
  colorScheme,
  getColor: (index: number | undefined) =>
    index === undefined
      ? colorScheme[Math.round(Math.random() * (colorScheme.length - 1))]
      : colorScheme[index % colorScheme.length],
};

export const useTheme = () => {
  return lightTheme;
};
